import { useMemo, useState } from 'react';

import { EventForUser } from 'legacy/events/types';
import { ErrorResponse } from 'legacy/common/utils/error';
import { useGetTierFromShareTokenCommand } from 'legacy/events/hooks';
import {
  useGetEventProducts,
  useGetUnauthenticatedEventProducts
} from 'api/queries/events/products';
import mapProductToTier from '../helpers/mapProductToTier';
import useAuthenticationStatus from 'legacy/common/hooks/useAuthenticationStatus';
import sortTiers from '../helpers/sortTiers';
import {
  isCodeAlreadyActive,
  isTierRequiringApproval,
  isTierDisabled
} from '../helpers/tierUtils';
import {
  groupTicketsBySection,
  filterSectionsWithTickets,
  createMenuSections
} from '../helpers/sectionUtils';
import { useTranslation } from 'react-i18next';

type UnlockPrivateTierFn = (
  code: string,
  options?: {
    onSuccess?: (tier: { tierId: string }) => void;
    onError?: (error: ErrorResponse) => void;
  }
) => void;

/**
 * Retrieves ticket tiers from an event.
 */
const useTiers = (event: EventForUser) => {
  const { t } = useTranslation(['checkout']);
  const { isAuthenticated } = useAuthenticationStatus();
  const [privateTierCodes, setPrivateTierCodes] = useState({});
  const { mutate, isLoading, error } = useGetTierFromShareTokenCommand();
  const { data: products, isLoading: isLoadingProducts } = useGetEventProducts(
    { eventId: event._id },
    {
      enabled: isAuthenticated()
    }
  );
  const {
    data: unauthenticatedProducts,
    isLoading: isLoadingUnauthenticatedProducts
  } = useGetUnauthenticatedEventProducts(
    { eventId: event._id },
    {
      enabled: !isAuthenticated()
    }
  );

  const mappedProductsToTiers = useMemo(() => {
    const productsToMap =
      products?.items || unauthenticatedProducts?.items || [];
    return productsToMap.map(mapProductToTier);
  }, [products?.items, unauthenticatedProducts?.items]);

  // Filter out private tiers but includes private tiers that have been unlocked AND sort the tickets by private first.
  const allTiers = useMemo(
    () =>
      mappedProductsToTiers
        .filter(
          (tier) =>
            tier.isVisible && (!tier.private || !!privateTierCodes[tier._id])
        )
        .sort(sortTiers),
    [mappedProductsToTiers, privateTierCodes]
  );

  // Get only private tiers that have been unlocked
  const privateTiers = useMemo(
    () => allTiers?.filter((tier) => tier.private),
    [allTiers]
  );

  // Filter out private tiers and sort by sortIndex
  const sortedTicketTiers = useMemo(
    () =>
      allTiers
        ?.filter((tier) => !tier.private)
        .sort((a, b) => a.sortIndex - b.sortIndex),
    [allTiers]
  );

  // Sort sections by sortIndex
  const sortedSections = useMemo(
    () =>
      (products?.sections || unauthenticatedProducts?.sections || [])?.sort(
        (a, b) => a.sortIndex - b.sortIndex
      ),
    [products?.sections || unauthenticatedProducts?.sections || []]
  );

  // Group tickets by section
  const ticketsBySection = useMemo(
    () => groupTicketsBySection(sortedTicketTiers),
    [sortedTicketTiers]
  );

  // Hide sections that don't have any tickets
  const filteredSections = useMemo(
    () => filterSectionsWithTickets(sortedSections, ticketsBySection),
    [sortedSections, ticketsBySection]
  );

  // Sections to display in the menu
  const menuSections = useMemo(
    () =>
      createMenuSections(
        privateTiers,
        filteredSections,
        ticketsBySection?.default || [],
        t
      ),
    [privateTiers, filteredSections, ticketsBySection]
  );

  /**
   * Sets the private tier code for a given tier ID.
   */
  const setPrivateTierCode = (tierId: string, code: string) => {
    setPrivateTierCodes((privateTierCodes) => ({
      ...privateTierCodes,
      [tierId]: code
    }));
  };

  /**
   * Function to unlock a private tier with a given code.
   */
  const unlockPrivateTier: UnlockPrivateTierFn = (code, options) => {
    mutate(
      {
        eventId: event._id,
        shareToken: code
      },
      {
        onSuccess: (tier) => {
          setPrivateTierCode(tier.tierId, code);
          options?.onSuccess?.(tier);
        },
        onError: (err) => {
          options?.onError?.(err);
        }
      }
    );
  };

  return {
    data: allTiers,
    privateTiers,
    tiersSections: sortedSections,
    sortedTicketTiers,
    ticketsBySection,
    filteredSections,
    menuSections,
    getTierFromShareToken: mutate,
    mappedProductsToTiers,
    getTierFromShareTokenError: error,
    isLoadingGetTierFromShareToken: isLoading,
    isLoadingProducts,
    isLoadingUnauthenticatedProducts,
    unlockPrivateTier,
    setPrivateTierCode,
    privateTierCodes,
    isCodeAlreadyActive: (code: string) =>
      isCodeAlreadyActive(privateTierCodes, code),
    isTierRequiringApproval: (code: string) =>
      isTierRequiringApproval(mappedProductsToTiers, code),
    isTierDisabled: (code: string) =>
      isTierDisabled(mappedProductsToTiers, code)
  };
};

export default useTiers;
